<script>
import { required, minLength, email } from "@vuelidate/validators";
import Container from "@/components/atoms/container";
import ButtonGroup from "@/components/molecules/button-group";
import Button from "@/components/atoms/button";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import Form from "@/components/molecules/form";
import FormError from "@/components/molecules/form/form-error";
import DividerHeading from "@/components/molecules/divider-heading";
import MeaCoraproLogin from "@/components/organisms/mise-en-avant-corapro/login";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import Captcha from "@/components/molecules/captcha";
import { useFideliteCarrefour } from "@/composables/fidelite_carrefour";

export default {
  name: "ConnexionForm",
  emits: ["connexion", "forgotPassword", "createAccount", "hidden"],
  components: {
    Container,
    ButtonGroup,
    Button,
    FormGroup,
    FormInput,
    Form,
    FormError,
    DividerHeading,
    MeaCoraproLogin,
    Captcha
  },
  props: {
    displayCreateAccount: {
      type: Boolean,
      required: false,
      default: true
    },
    captchaKey: {
      type: String,
      required: false,
      default: null
    },
    ...formValidationProps()
  },
  setup(props) {
    const { isTemps2Fidelite } = useFideliteCarrefour();
    const captcha = ref(null);
    const { v$, getState, checkValidation, states } = useFormValidation(
      props,
      captcha
    );
    return {
      captcha,
      v$,
      getState,
      checkValidation,
      states,
      isTemps2Fidelite
    };
  },
  data() {
    return {
      credentials: {
        email: "",
        password: ""
      }
    };
  },
  validations() {
    return {
      credentials: {
        email: {
          required,
          email
        },
        password: {
          required,
          minLength: minLength(6)
        }
      }
    };
  },
  methods: {
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      this.$emit("connexion", {
        credentials: this.credentials,
        captcha: this.captchaToken
      });
    },
    connexion() {
      if (this.checkValidation(this.v$)) {
        if (this.captchaKey) {
          this.verifyCaptcha(this.captchaKey);
        } else {
          this.$refs.captcha.execute();
        }
      }
    }
  }
};
</script>

<template>
  <Container>
    <h1 class="c-connexion-form__title">
      Déjà client
    </h1>
    <p class="c-connexion-form__explanation">
      Si vous avez déjà un compte sur cora.fr ou
      sur l’application, utilisez-le pour vous connecter.
    </p>
    <Form @submit.prevent="connexion()">
      <FormGroup
        :state="getState(v$,'email', 'credentials')"
        size="lg"
        class="c-connexion-form__form-group u-pb-xs"
      >
        <FormInput
          v-model.trim="credentials.email"
          :state="getState(v$,'email', 'credentials')"
          size="lg"
          type="email"
          aria-describedby="emailMessage"
          @blur="v$.credentials.email.$touch()"
          @input="v$.credentials.email.$reset()"
        >
          Adresse email *
        </FormInput>
        <template #message>
          <div id="emailMessage">
            <p v-if="v$.credentials.email.required.$invalid">
              Merci de renseigner votre adresse e-mail
            </p>
            <p v-if="v$.credentials.email.email.$invalid">
              Votre adresse email est invalide, elle doit comporter un @ et un .
            </p>
          </div>
        </template>
      </FormGroup>

      <FormGroup
        :state="getState(v$,'password', 'credentials')"
        size="lg"
        class="c-connexion-form__form-group"
      >
        <FormInput
          v-model.trim="credentials.password"
          :state="getState(v$,'password', 'credentials')"
          type="password"
          size="lg"
          aria-describedby="passwordMessage"
          @blur="v$.credentials.password.$touch()"
          @input="v$.credentials.password.$reset()"
        >
          Mot de passe *
        </FormInput>
        <template #message>
          <div id="passwordMessage">
            <p v-if="v$.credentials.password.required.$invalid">
              Merci de renseigner votre mot de passe
            </p>
            <template v-if="v$.credentials.password.minLength.$invalid">
              <p>Votre mot de passe est incorrect</p>
              <p class="u-color-grey-1">
                Pour rappel votre mot de passe doit comporter au moins 6 caractères.
              </p>
            </template>
          </div>
        </template>
      </FormGroup>
      <FormError
        v-if="error"
        :error="error"
        class="u-text-center"
      />

      <!-- <FormGroup
        v-if="false"
        class="c-connexion-form__form-group u-mt-md"
      >
        <FormCheckbox>Rester connecté(e)</FormCheckbox>
      </FormGroup> -->
      <Captcha
        v-if="!captchaKey"
        ref="captcha"
        @verify="verifyCaptcha"
      />

      <ButtonGroup class="u-flex-column">
        <Button
          variant="secondary"
          radius="full"
          type="submit"
          class="c-connexion-form__button-connexion"
          value="Je me connecte"
          size="md"
          :disabled="state === states.LOADING"
        />

        <div
          class="u-flex"
          :class="isTemps2Fidelite ? 'u-flex-wrap u-flex-justify-between u-full-width u-max-width-30 u-mx-auto' : 'u-gap-lg20 u-align-center'"
        >
          <Button
            class="c-connexion-form__mdp-forgotten"
            link
            @click="$emit('forgotPassword')"
          >
            Mot de passe oublié ?
          </Button>
        </div>
      </ButtonGroup>
    </Form>
    <template v-if="displayCreateAccount">
      <DividerHeading
        dotted
        class="u-mt-md"
      >
        <h2 class="c-connexion-form__title">
          Nouveau client
        </h2>
      </DividerHeading>
      <p class="c-connexion-form__explanation">
        En créant votre compte vous pourrez aussi créer votre carte de fidélité.
      </p>
      <div class="u-text-center u-mt-md u-pb-md">
        <Button
          outline
          radius="full"
          variant="primary"
          size="md"
          @click="$emit('createAccount')"
        >
          Je crée mon compte
        </Button>
      </div>
    </template>
    <div class="u-py-md">
      <MeaCoraproLogin
        accueil
        class="u-max-width-35 u-full-width u-mx-auto"
        @hidden="$emit('hidden')"
      />
    </div>
  </Container>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/connexion-form.scss";
</style>
